import * as React from 'react';
import { Box, Button, Stack, SxProps, Theme, Typography } from '@mui/material';
import { Form, useFormikContext } from 'formik';
import { Discounts } from '@prisma/client';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import {
  CheckoutFormValues,
  CheckoutPaymentMethod,
  SelectedAppointment,
} from '../../types/appointments';
import { SplitPaymentOnFinishArgs } from '../../types/payments';
import { getWaiverRequestStatus } from '../../helpers/waivers';
import useGetCheckoutData from '../../hooks/checkouts/useGetCheckoutData';
import useCheckoutPricing from '../../hooks/checkouts/useCheckoutPricing';
import useBusinessWebRedesign from '../../hooks/businesses/useBusinessWebRedesign';
import useCheckoutStep from '../../hooks/checkouts/useCheckoutStep';
import { formatDollarAmount } from '../../utils/global/helper';
import ClientSearchAutofill from '../form/ClientSearchAutofill';
import CheckoutDetails from '../form/CheckoutDetails';
import PricingTable from '../form/PricingTable';
import PaymentMethod from '../form/PaymentMethod';
import ColoredAlert from '../global/ColoredAlert';
import SplitPaymentFlow from '../payments/SplitPaymentFlow';
import { ModalHeaderAside, ModalHeaderBadge } from '../modal/FullPageModal';
import CheckoutLineItems from './CheckoutLineItems';
import AddLineItemButtons from './AddLineItemButtons';
import CheckoutOrderSummary from './CheckoutOrderSummary';
import CheckoutPricingModals from './CheckoutPricingModals';
import './checkout.css';

interface Props {
  selectedAppointment: SelectedAppointment | null;
  appointment: SelectedAppointment | null;
  setAppointment: React.Dispatch<
    React.SetStateAction<SelectedAppointment | null>
  >;
  selectedDiscount?: Discounts | null;
  checkoutData: ReturnType<typeof useGetCheckoutData>;
  enabledPaymentMethods: CheckoutPaymentMethod[];
  formError: string;
  waiverStatus: ReturnType<typeof getWaiverRequestStatus>;
  submitText: string;
  submitDisabled: boolean;
  isSplitPayment: boolean;
  onSplitPayment: () => void;
  onCloseSplitPayment: () => void;
  onCheckoutSuccess: (args: SplitPaymentOnFinishArgs) => void;
  onRefreshAppointment: () => void | Promise<void>;
}

export default function CheckoutBody({
  selectedAppointment,
  appointment,
  setAppointment,
  selectedDiscount,
  checkoutData,
  enabledPaymentMethods,
  formError,
  waiverStatus,
  submitText,
  submitDisabled,
  isSplitPayment,
  onSplitPayment,
  onCloseSplitPayment,
  onCheckoutSuccess,
  onRefreshAppointment,
}: Props) {
  const { values, setFieldValue, isSubmitting, submitForm } =
    useFormikContext<CheckoutFormValues>();
  const { active: redesignActive } = useBusinessWebRedesign();
  const { step, setStep } = useCheckoutStep();

  const pricing = useCheckoutPricing({
    checkoutData,
    selectedDiscount,
    appointment,
    selectedAppointment,
    values,
    setFieldValue,
    setAppointment,
    onRefreshAppointment,
  });

  // The design leads with the client card itself; the classic layout needs the
  // heading to tie the client and appointment cards together.
  const clientSectionTitle = redesignActive
    ? null
    : (selectedAppointment && 'Client & Appointment') || undefined;

  const clientSection = (
    <Box className="checkout-client">
      <ClientSearchAutofill
        title={clientSectionTitle}
        hideBirthday
        namePrefix={['customer']}
        selectedRequest={appointment ?? undefined}
        allowCustomerWithoutAppointment
      />
    </Box>
  );

  const appointmentSection = (
    <CheckoutDetails
      selectedAppointment={selectedAppointment}
      appointment={appointment}
      onSelectAppointment={setAppointment}
    />
  );

  const waiverAlert = (containerSx: SxProps<Theme>) =>
    (waiverStatus === 'notCompleted' || waiverStatus === 'pendingEmployee') && (
      <ColoredAlert
        type="error"
        title="Waiver Incomplete"
        subtitle={
          waiverStatus === 'pendingEmployee'
            ? 'The customer has signed the waiver, but the employee portion is still pending. Please complete the employee section of the waiver before checking out.'
            : 'The waiver for this appointment has not been signed by the customer. Please send or resend the waiver to the customer and have them complete it before checking out.'
        }
        dismissible={false}
        containerSx={containerSx}
      />
    );

  const errorAlert = (containerSx: SxProps<Theme>) =>
    !!formError && (
      <ColoredAlert
        animate={false}
        dismissible
        type="error"
        title="Checkout Error"
        subtitle={formError}
        containerSx={containerSx}
      />
    );

  const paymentSection = (
    <PaymentMethod
      checkoutData={checkoutData}
      enabledPaymentMethods={enabledPaymentMethods}
      selectedAppointment={appointment}
      selectedDiscount={selectedDiscount}
      onSplitPayment={onSplitPayment}
    />
  );

  const sharedModals = (
    <>
      <CheckoutPricingModals pricing={pricing} />
      {!!checkoutData.data && (
        <SplitPaymentFlow
          isOpen={isSplitPayment}
          onFinish={onCheckoutSuccess}
          onClose={onCloseSplitPayment}
          checkoutData={checkoutData.data}
        />
      )}
    </>
  );

  if (!redesignActive) {
    return (
      <>
        {waiverAlert({ width: '100%', mb: 2 })}
        {clientSection}
        <Form>
          {appointmentSection}
          {errorAlert({ width: '100%', my: -2 })}
          <PricingTable pricing={pricing} />
          {paymentSection}
          <Button
            type="submit"
            disabled={submitDisabled}
            variant="contained"
            className="primary">
            {submitText}
          </Button>
        </Form>
        {sharedModals}
      </>
    );
  }

  const isItemsStep = step === 'items';
  const tipFootNote = pricing.isTipAvailable
    ? 'Add a tip on the payment step'
    : undefined;

  return (
    <Box className="checkout-wide">
      <ModalHeaderBadge
        variant="plain"
        label={isItemsStep ? 'Step 1 of 2 · Items' : 'Step 2 of 2 · Payment'}
      />
      <ModalHeaderAside>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <span>Total due</span>
          <Typography variant="h2" component="span" color="text.primary">
            {formatDollarAmount(pricing.summary.total)}
          </Typography>
        </Stack>
      </ModalHeaderAside>

      <Box className="checkout-body">
        <Box className="checkout-sections">
          {isItemsStep && waiverAlert({ width: '100%' })}
          {errorAlert({ width: '100%' })}
          {/* Both steps stay mounted: remounting them would re-run the
              appointment section's commission default and re-mount the card
              element every time someone stepped back. */}
          <Box
            className="checkout-step"
            display={isItemsStep ? 'flex' : 'none'}>
            {clientSection}
            {appointmentSection}
            <CheckoutLineItems
              title={pricing.itemsTitle}
              items={pricing.items}
              discount={pricing.discountLine}
            />
            <AddLineItemButtons pricing={pricing} />
          </Box>
          <Form>
            <Box
              className="checkout-step"
              display={isItemsStep ? 'none' : 'flex'}>
              <Button
                onClick={() => setStep('items')}
                sx={{
                  alignSelf: 'flex-start',
                  gap: 1,
                  minWidth: 0,
                  padding: 0,
                  textTransform: 'none',
                  color: 'var(--medGrey)',
                  '&:hover': {
                    background: 'transparent',
                    color: theme => theme.palette.common.black,
                  },
                  typography: 'caption',
                  fontWeight: 600,
                }}>
                <ChevronLeftRounded sx={{ fontSize: 17 }} />
                Back to items
              </Button>
              {paymentSection}
            </Box>
          </Form>
        </Box>

        <Box className="checkout-summary">
          <CheckoutOrderSummary
            summary={pricing.summary}
            submitText={isItemsStep ? 'Go to payment' : submitText}
            disabled={!isItemsStep && submitDisabled}
            isSubmitting={isSubmitting}
            onSubmit={isItemsStep ? () => setStep('payment') : submitForm}
            footNote={isItemsStep ? tipFootNote : pricing.depositNote}
          />
        </Box>
      </Box>
      {sharedModals}
    </Box>
  );
}
